import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCheckbox } from '../primitives/CDCheckbox';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface CashToCloseLineItem {
  label: string;
  loanEstimate?: number;
  final?: number;
  didChange?: boolean;
}

export interface CalculatingCashToCloseData {
  totalClosingCosts: CashToCloseLineItem;
  closingCostsPaidBeforeClosing: CashToCloseLineItem;
  closingCostsFinanced: CashToCloseLineItem;
  downPaymentFromBorrower: CashToCloseLineItem;
  deposit: CashToCloseLineItem;
  fundsForBorrower: CashToCloseLineItem;
  sellerCredits: CashToCloseLineItem;
  adjustmentsAndOtherCredits: CashToCloseLineItem;
  cashToClose: CashToCloseLineItem;
}

export interface CDCalculatingCashToCloseProps {
  /** Cash to close data */
  data: Partial<CalculatingCashToCloseData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<CalculatingCashToCloseData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDCalculatingCashToClose - Calculating Cash to Close section on Page 3
 *
 * Compares Loan Estimate figures with Final figures and shows changes.
 */
export const CDCalculatingCashToClose = React.forwardRef<
  HTMLDivElement,
  CDCalculatingCashToCloseProps
>(({ data, editable = false, onChange, className }, ref) => {
  // Helper to update a specific line item
  const updateLineItem = (
    field: keyof CalculatingCashToCloseData,
    updates: Partial<CashToCloseLineItem>,
  ) => {
    const current = data[field] || { label: '' };
    onChange?.({
      ...data,
      [field]: { ...current, ...updates },
    });
  };

  const renderRow = (
    item: CashToCloseLineItem | undefined,
    field: keyof CalculatingCashToCloseData,
    isTotal = false,
  ) => {
    if (!item) return null;
    return (
      <tr className={cn('cd-text-small', isTotal && 'font-bold bg-cd-gray-light')}>
        <td className={cn('border border-cd-black px-2 py-1', isTotal && 'border-t-2')}>
          <CDEditableText
            value={item.label}
            editable={editable}
            onChange={(v) => updateLineItem(field, { label: v })}
            size="small"
            bold={isTotal}
          />
        </td>
        <td
          className={cn(
            'border border-cd-black px-2 py-1 text-right w-24',
            isTotal && 'border-t-2',
          )}
        >
          <CDCurrency
            value={item.loanEstimate}
            editable={editable}
            onChange={(v) => updateLineItem(field, { loanEstimate: v })}
            className="cd-text-small"
          />
        </td>
        <td
          className={cn(
            'border border-cd-black px-2 py-1 text-right w-24',
            isTotal && 'border-t-2',
          )}
        >
          <CDCurrency
            value={item.final}
            editable={editable}
            onChange={(v) => updateLineItem(field, { final: v })}
            className="cd-text-small"
          />
        </td>
        <td
          className={cn(
            'border border-cd-black px-2 py-1 text-center w-20',
            isTotal && 'border-t-2',
          )}
        >
          <CDCheckbox
            checked={item.didChange || false}
            size="sm"
            onChange={
              editable ? (checked) => updateLineItem(field, { didChange: checked }) : undefined
            }
          />
        </td>
      </tr>
    );
  };

  return (
    <div ref={ref} className={cn('', className)}>
      <CDSectionHeader title="Calculating Cash to Close" />

      <table className="w-full border-collapse border-t-0">
        <thead>
          <tr className="cd-text-tiny bg-cd-gray-light">
            <th className="border border-t-0 border-cd-black px-2 py-1 text-left"></th>
            <th className="border border-t-0 border-cd-black px-2 py-1 text-center w-24">
              Loan Estimate
            </th>
            <th className="border border-t-0 border-cd-black px-2 py-1 text-center w-24">Final</th>
            <th className="border border-t-0 border-cd-black px-2 py-1 text-center w-20">
              Did this change?
            </th>
          </tr>
        </thead>
        <tbody>
          {renderRow(data.totalClosingCosts, 'totalClosingCosts')}
          {renderRow(data.closingCostsPaidBeforeClosing, 'closingCostsPaidBeforeClosing')}
          {renderRow(data.closingCostsFinanced, 'closingCostsFinanced')}
          {renderRow(data.downPaymentFromBorrower, 'downPaymentFromBorrower')}
          {renderRow(data.deposit, 'deposit')}
          {renderRow(data.fundsForBorrower, 'fundsForBorrower')}
          {renderRow(data.sellerCredits, 'sellerCredits')}
          {renderRow(data.adjustmentsAndOtherCredits, 'adjustmentsAndOtherCredits')}
          {renderRow(data.cashToClose, 'cashToClose', true)}
        </tbody>
      </table>

      <div className="cd-text-tiny text-cd-gray-text mt-1 p-1 border border-t-0 border-cd-black italic">
        Use this table to see what has changed from your Loan Estimate.
      </div>
    </div>
  );
});

CDCalculatingCashToClose.displayName = 'CDCalculatingCashToClose';
